import { useEffect, useState } from 'react';
import type { UseActiveMaintenanceResult } from '../../hooks/useActiveMaintenance';
import { colors } from '../../styles/theme';
import { useMovablePopup } from '../MovablePopup';

/** Fire the warning when the session is this close to downtime (#54). */
export const PRE_WINDOW_MS = 15 * 60_000;
const AUTO_DISMISS_MS = 30_000;

/** True when `msUntilStart` falls inside the pre-maintenance warning band:
 *  downtime hasn't started yet (> 0) but is within `windowMs`. Pure — unit
 *  tested separately. */
export function isWithinPreMaintenance(
  msUntilStart: number | null,
  windowMs: number = PRE_WINDOW_MS,
): boolean {
  return msUntilStart !== null && msUntilStart > 0 && msUntilStart <= windowMs;
}

interface Props {
  readonly data: UseActiveMaintenanceResult;
}

/**
 * Pre-maintenance alert (#54): a one-time modal that fires when an
 * authenticated session is within 15 minutes of a scheduled window, telling
 * the user to save their work before the session ends. Has a Dismiss button
 * and auto-dismisses after 30s. Keyed by the window's start so a re-poll (or
 * the per-second tick) doesn't re-pop it once dismissed.
 */
export default function MaintenancePreModal({ data }: Props): JSX.Element | null {
  const { window: w, msUntilStart } = data;
  const popup = useMovablePopup<HTMLDivElement>();
  const startsAt = w?.starts_at ?? null;
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const visible = isWithinPreMaintenance(msUntilStart) && dismissedFor !== startsAt;

  // Auto-dismiss after 30s while visible.
  useEffect(() => {
    if (!visible || startsAt === null) return undefined;
    const id = globalThis.setTimeout(() => setDismissedFor(startsAt), AUTO_DISMISS_MS);
    return () => globalThis.clearTimeout(id);
  }, [visible, startsAt]);

  if (!visible || !w) return null;

  const minutes = Math.max(1, Math.ceil((msUntilStart ?? 0) / 60_000));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(6,9,26,0.55)' }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="pre-maint-title"
    >
      <div
        ref={popup.panelRef}
        className="w-full max-w-md rounded-2xl p-6"
        style={{ ...popup.panelStyle, background: colors.surface }}
      >
        {popup.bar}
        <h2 id="pre-maint-title" className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
          Scheduled maintenance soon
        </h2>
        <p className="text-sm mb-5" style={{ color: colors.inkSoft }}>
          This session will end in about {minutes} minute{minutes === 1 ? '' : 's'} due to
          scheduled maintenance ({w.reason_short}). Please save your work.
        </p>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setDismissedFor(startsAt)}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: colors.navy, color: '#FFFFFF' }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
